import React, { useEffect, useMemo, useState } from 'react';

import {
  Card,
  Col,
  Flex,
  Form,
  type FormInstance,
  Row,
  Space,
  Tooltip,
} from 'antd-v5';
import { RuleObject } from 'antd-v5/es/form';
import _, { isEmpty } from 'lodash';
import { MdInfo, MdOutlineErrorOutline } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Heading, Input, Select, Text } from 'scouting-ui/lib';
import { cssVars } from 'scouting-ui/lib/variables';

import type { MeritBadge } from '@appTypes/esb';
import { districtsSel, loadDistrictsRequest } from '@modules/councilUnits';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import MBImage from '@modules/meritBadgeCounselors/mbcDashboard/components/MBImage';
import { organizationTypeId } from '@shared/constants';
import { intl } from '@shared/localization';
import { useActiveOrganization } from '@unit/duck/hooks';
import { usePositions } from '@user/duck/hooks';

import {
  ALL_MERIT_BADGES,
  ANY_COUNCIL_VALUE,
  AvailabilityFilterOptions,
  MBCSearchFields,
  NON_US_VALUE,
  ProximityFilterOptions,
  mbcSearchOptions,
  proximityFilterId,
  usersLevelCouncil,
  usersLevelDistrict,
  usersLevelUnit,
} from '../../constants';
import {
  type SelectOption,
  getDefaultSearchType,
  getOrgOptions,
} from '../../duck/utils';
import styles from './MbcSearch.less';

const FormItem = Form.Item;

const wildCardMinimumValidation = {
  validator: async (_: RuleObject, value: string) => {
    if (!value) return value;
    const nonWildcardChars = value.replaceAll(/[%*]/g, '').trim();
    const hasWildcard = value.includes('%') || value.includes('*');

    if (hasWildcard && nonWildcardChars.length < 3) {
      throw new Error(
        intl.formatMessage({
          id: 'page.mbcSearch.names.minCharactersWithWildcard',
        }),
      );
    }

    if (nonWildcardChars.length < 2) {
      throw new Error(
        intl.formatMessage({
          id: 'page.mbcSearch.names.minCharacters',
        }),
      );
    }

    return value;
  },
};

const wildCardProhibitionValidation = {
  validator: async (_: RuleObject, value: string) => {
    if (!value) return value;
    const hasWildcard = value.includes('%') || value.includes('*');

    if (hasWildcard) {
      throw new Error(
        intl.formatMessage({
          id: 'page.mbcSearch.lastName.noWIldcard',
        }),
      );
    }

    return value;
  },
};

interface MbcSearchProps {
  defaultMeritBadge?: number;
  mbcSearchLoading: boolean;
  mbDictionaryLoading: boolean;
  allMeritBadges: MeritBadge[];
  editMBsWarning?: string;
  handleEditMBsWarning?: () => void;
}

const MbcSearch: React.FC<MbcSearchProps> = ({
  defaultMeritBadge,
  mbcSearchLoading,
  mbDictionaryLoading,
  allMeritBadges,
  editMBsWarning,
  handleEditMBsWarning,
}) => {
  const dispatch = useDispatch();
  const districts = useSelector(districtsSel);

  useEffect(() => {
    //TODO: migrate to RTK
    dispatch(loadDistrictsRequest());
  }, [dispatch]);
  const form: FormInstance = Form.useFormInstance();
  const [disableZip, setDisableZip] = useState(false);
  const [disableProximity, setDisableProximity] = useState(false);
  const currentMemberId = Form.useWatch(MBCSearchFields.MEMBER_ID, form);
  const { hasPositions } = usePositions();
  const currentOrg = useActiveOrganization();
  const unitInfo = currentOrg.data;
  const isDistrict =
    unitInfo?.organizationTypeId === organizationTypeId.COUNCIL_ENTITY;
  const isCouncil = unitInfo?.organizationTypeId === organizationTypeId.COUNCIL;
  const isUnit = unitInfo?.organizationTypeId === organizationTypeId.UNIT;
  const isMBListLoading = mbDictionaryLoading || mbcSearchLoading;
  const hasCouncilLevelPosition = hasPositions(usersLevelCouncil);
  const hasDistrictLevelPosition = hasPositions(usersLevelDistrict);
  const hasUnitLevelPosition = hasPositions(usersLevelUnit);

  const mbOptions = useMemo(() => {
    const mbs = _.chain(allMeritBadges)
      .orderBy(['name'], ['asc'])
      .map((mb: MeritBadge) => ({
        image: mb.imageUrl100,
        label: (
          <div className={styles.meritBadgeOption}>
            <Flex align="center" gap={4}>
              <MBImage src={mb.imageUrl100} height="auto" width={24} />
            </Flex>
            <span>{mb.name}</span>
          </div>
        ),
        text: mb.name,
        value: mb.id,
      }))
      .value();

    const excludedInUnit = ['Key 3 Delegate', 'Registration Inquiry'];

    const conditionalOptions = featureFlags.getFlag(
      'SBL_6790_ENABLE_NO_MB_FILTER',
    )
      ? mbcSearchOptions.filter(option => {
          let allowedPositions: string[] = option.allowedPositions;

          if (isUnit) {
            allowedPositions = _.difference(allowedPositions, excludedInUnit);
          }

          return hasPositions(allowedPositions) || !allowedPositions?.length;
        })
      : [ALL_MERIT_BADGES];

    return [...conditionalOptions, ...mbs];
  }, [allMeritBadges, hasPositions, isUnit]);

  const availabilityOptions = Object.values(AvailabilityFilterOptions).map(
    value => ({
      label: value,
      value,
    }),
  );

  const councilDistrictOptions = useMemo(
    () =>
      [
        {
          label: intl.formatMessage({
            id: 'page.mbcSearch.checkbox.anyCouncil',
          }),
          value: ANY_COUNCIL_VALUE,
        },
        {
          label: intl.formatMessage({
            id: 'page.mbcSearch.checkbox.outsideUS',
          }),
          value: NON_US_VALUE,
        },
        ...getOrgOptions(unitInfo, districts, {
          hasCouncilLevelPosition,
          hasDistrictLevelPosition,
          hasUnitLevelPosition,
          isDistrict,
          isUnit,
        }),
      ].filter(Boolean) as SelectOption[],
    [
      hasCouncilLevelPosition,
      hasDistrictLevelPosition,
      hasUnitLevelPosition,
      isDistrict,
      isUnit,
      unitInfo,
      districts,
    ],
  );
  const defaultSearchType = getDefaultSearchType(
    isCouncil,
    isDistrict,
    isUnit,
    hasCouncilLevelPosition,
    hasDistrictLevelPosition,
    hasUnitLevelPosition,
    unitInfo,
  );

  useEffect(() => {
    const currentValue = form.getFieldValue(MBCSearchFields.ORG_GUID_TYPE);
    if (!currentValue && councilDistrictOptions.length > 0) {
      form.setFieldValue(MBCSearchFields.ORG_GUID_TYPE, defaultSearchType);
    }

    if (currentValue === NON_US_VALUE) {
      form.setFieldValue(MBCSearchFields.ZIP_CODE, '');
      form.setFieldValue(MBCSearchFields.PROXIMITY, proximityFilterId.MILES_5);

      setDisableZip(true);
      setDisableProximity(true);
      handleEditMBsWarning?.();
    }
  }, [form, councilDistrictOptions, defaultSearchType, handleEditMBsWarning]);

  // Proximity
  const proximityOptions = ProximityFilterOptions.map(mb => ({
    label: mb.label,
    value: mb.id,
  }));

  const handleCouncilDistrictChange = (value: string) => {
    if (value === NON_US_VALUE) {
      form.setFieldValue(MBCSearchFields.ZIP_CODE, '');
      form.setFieldValue(MBCSearchFields.PROXIMITY, proximityFilterId.MILES_5);

      setDisableZip(true);
      setDisableProximity(true);
    } else {
      setDisableZip(false);
      setDisableProximity(false);
    }
    handleEditMBsWarning?.();
  };

  return (
    <Card>
      <Space direction="vertical" size={10}>
        <Row>
          <Col xs={24}>
            <Heading font="display-h5">
              <FormattedMessage id="mbc.mbcSearch" />
            </Heading>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} lg={8}>
            <FormItem name={MBCSearchFields.MERIT_BADGES}>
              <Select
                showSearch
                options={mbOptions}
                placeholder={
                  <FormattedMessage id="page.mbcSearch.meritBadges" />
                }
                loading={isMBListLoading}
                disabled={isMBListLoading || !!defaultMeritBadge}
                filterOption={true}
                optionFilterProp={'text'}
              />
            </FormItem>
          </Col>
          <Col xs={24} lg={8}>
            <FormItem
              name={MBCSearchFields.ORG_GUID_TYPE}
              dependencies={[MBCSearchFields.ORG_GUID_TYPE]}
              rules={[
                ({ getFieldValue }) => ({
                  required: !getFieldValue(MBCSearchFields.ORG_GUID_TYPE),
                  message: 'Required if input is empty',
                }),
              ]}
            >
              <Select
                defaultValue={defaultSearchType ?? undefined}
                placeholder={
                  <FormattedMessage id="page.mbcSearch.councilDistrict" />
                }
                loading={mbcSearchLoading}
                disabled={mbcSearchLoading}
                options={councilDistrictOptions}
                onChange={handleCouncilDistrictChange}
              />
            </FormItem>
          </Col>
          <Col xs={24} lg={8}>
            <FormItem name={MBCSearchFields.AVAILABILITY}>
              <Select
                placeholder={
                  <FormattedMessage id="page.mbcSearch.availability" />
                }
                loading={mbcSearchLoading}
                disabled={mbcSearchLoading}
                options={availabilityOptions}
              />
            </FormItem>
          </Col>
          <Col xs={24} lg={6}>
            <FormItem
              name={MBCSearchFields.FIRST_NAME}
              validateTrigger="onSubmit"
              rules={[
                ({ getFieldValue }) => ({
                  required:
                    !isEmpty(getFieldValue(MBCSearchFields.LAST_NAME)) ||
                    !isEmpty(getFieldValue(MBCSearchFields.EMAIL)) ||
                    !isEmpty(getFieldValue(MBCSearchFields.MEMBER_ID)),
                  message: (
                    <FormattedMessage id="page.mbcSearch.firstName.required" />
                  ),
                }),
                wildCardMinimumValidation,
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: 'page.mbcSearch.firstName',
                })}
                allowClear
                disabled={mbcSearchLoading}
              />
            </FormItem>
          </Col>
          <Col xs={24} lg={6}>
            <FormItem
              name={MBCSearchFields.LAST_NAME}
              validateTrigger="onSubmit"
              rules={[
                ({ getFieldValue }) => ({
                  required:
                    !isEmpty(getFieldValue(MBCSearchFields.FIRST_NAME)) ||
                    !isEmpty(getFieldValue(MBCSearchFields.EMAIL)) ||
                    !isEmpty(getFieldValue(MBCSearchFields.MEMBER_ID)),
                  message: (
                    <FormattedMessage id="page.mbcSearch.lastName.required" />
                  ),
                }),
                currentMemberId
                  ? wildCardProhibitionValidation
                  : wildCardMinimumValidation,
              ]}
            >
              <Input
                prefix={
                  currentMemberId ? (
                    <Tooltip
                      title={
                        <FormattedMessage id="page.mbcSearch.lastName.warning" />
                      }
                    >
                      <MdOutlineErrorOutline className={styles.lastnameIcon} />
                    </Tooltip>
                  ) : undefined
                }
                placeholder={intl.formatMessage({
                  id: 'page.mbcSearch.lastName',
                })}
                allowClear
                disabled={mbcSearchLoading}
              />
            </FormItem>
          </Col>
          <Col xs={24} lg={6}>
            <FormItem
              name={MBCSearchFields.EMAIL}
              validateTrigger="onSubmit"
              rules={[
                ({ getFieldValue }) => ({
                  required:
                    (!isEmpty(getFieldValue(MBCSearchFields.FIRST_NAME)) ||
                      !isEmpty(getFieldValue(MBCSearchFields.LAST_NAME))) &&
                    isEmpty(getFieldValue(MBCSearchFields.MEMBER_ID)),
                  message: (
                    <FormattedMessage id="page.mbcSearch.email.required" />
                  ),
                }),
                {
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: intl.formatMessage({
                    id: 'page.mbcSearch.email.invalid',
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: 'page.mbcSearch.email',
                })}
                allowClear
                disabled={mbcSearchLoading}
              />
            </FormItem>
          </Col>

          <Col xs={24} lg={6}>
            <FormItem
              name={MBCSearchFields.MEMBER_ID}
              validateTrigger="onSubmit"
              rules={[
                ({ getFieldValue }) => ({
                  required:
                    (!isEmpty(getFieldValue(MBCSearchFields.FIRST_NAME)) ||
                      !isEmpty(getFieldValue(MBCSearchFields.LAST_NAME))) &&
                    isEmpty(getFieldValue(MBCSearchFields.EMAIL)),
                  message: (
                    <FormattedMessage id="page.mbcSearch.memberId.required" />
                  ),
                }),
                {
                  pattern: /^\d{5,}$/,
                  message: intl.formatMessage({
                    id: 'page.mbcSearch.memberId.invalid',
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: 'page.mbcSearch.memberId',
                })}
                allowClear
                disabled={mbcSearchLoading}
              />
            </FormItem>
          </Col>
          <Col xs={24} lg={8}>
            <FormItem
              name={MBCSearchFields.ZIP_CODE}
              rules={[
                {
                  pattern: /^\d{5}(-\d{4})?$/,
                  message: intl.formatMessage({
                    id: 'page.mbcSearch.zipCode.invalid',
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: 'page.mbcSearch.zipCode',
                })}
                allowClear
                disabled={mbcSearchLoading || disableZip}
                suffix={
                  <Tooltip
                    placement="bottom"
                    title={
                      <FormattedHTMLMessage id="page.mbcSearch.zipCode.note" />
                    }
                  >
                    <MdInfo
                      className={styles.infoIcon}
                      color={cssVars['color-info']}
                    />
                  </Tooltip>
                }
              />
            </FormItem>
          </Col>
          <Col xs={24} lg={8}>
            <FormItem name={MBCSearchFields.PROXIMITY}>
              <Select
                placeholder={<FormattedMessage id="page.mbcSearch.proximity" />}
                loading={mbcSearchLoading}
                disabled={mbcSearchLoading || disableProximity}
                options={proximityOptions}
              />
            </FormItem>
          </Col>
        </Row>
        <Row justify="end">
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={mbcSearchLoading}
              disabled={mbcSearchLoading}
            >
              <FormattedMessage id="page.mbcSearch.button.search" />
            </Button>
          </Col>
        </Row>
        {editMBsWarning && (
          <Row justify="start">
            <Col>
              <Space align="center" size={4}>
                <MdInfo
                  className={styles.iconInfo}
                  color={cssVars['color-info']}
                />
                <Text font="body-caption" italic>
                  <FormattedMessage id={editMBsWarning} />
                </Text>
              </Space>
            </Col>
          </Row>
        )}
      </Space>
    </Card>
  );
};

export default MbcSearch;
